import { RouterProvider } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect } from 'react';
import { iframeLocalStorage } from 'chat-ui/src/utils/iframeLocalStorage';
import { EnvironmentTypes } from '@common/constants';
import { getErrorMessage } from '@common/utils';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import router from './router';

const isNonDev = import.meta.env.MODE !== EnvironmentTypes.DEVELOPMENT;

// Initialize Sentry with core integrations only — replay is deferred after first paint
Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  enabled: isNonDev,
  environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || EnvironmentTypes.PRODUCTION,
  integrations: [Sentry.browserTracingIntegration()],
  // Performance Monitoring
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  tracePropagationTargets: ['*'],
  // Session Replay (applied when replay integration loads)
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,

  // Better error grouping for similar issues
  attachStacktrace: true,

  // Allow for custom fingerprinting rules
  beforeSend(event, hint) {
    // Add release information when available in env
    if (import.meta.env.VITE_APP_VERSION) {
      event.release = import.meta.env.VITE_APP_VERSION;
    }

    const error = hint?.originalException as any;
    const message = getErrorMessage(error);

    // Filter out asset loading errors (temporary fix to reduce Sentry noise)
    const assetErrorPatterns = ['Uncompressed Asset', 'Large Render Blocking Asset'];

    if (
      assetErrorPatterns.some((pattern) => message.toLowerCase().includes(pattern.toLowerCase()))
    ) {
      return null;
    }

    return event;
  },

  // Custom error tags
  initialScope: {
    tags: {
      app: 'chat',
      environment: import.meta.env.MODE || EnvironmentTypes.PRODUCTION,
    },
  },
});

// Defer non-critical initializations until after first paint
if (isNonDev) {
  const deferInit = () => {
    // Add Sentry replay integration after first paint
    Sentry.addIntegration(
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    );

    // Initialize PostHog analytics (only if API key is configured)
    if (import.meta.env.VITE_PUBLIC_POSTHOG_KEY) {
      try {
        posthog.init(import.meta.env.VITE_PUBLIC_POSTHOG_KEY, {
          api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
          person_profiles: 'identified_only',
          disable_session_recording: import.meta.env.MODE !== EnvironmentTypes.PRODUCTION,
          loaded: (posthogInstance) => {
            posthogInstance.register({
              app: 'chat',
              environment: import.meta.env.MODE || EnvironmentTypes.PRODUCTION,
            });
          },
        });
      } catch {
        // Analytics failure should not break the application
      }
    }
  };

  if ('requestIdleCallback' in window) {
    requestIdleCallback(deferInit);
  } else {
    setTimeout(deferInit, 2000);
  }
}

const queryClient = new QueryClient();

function Root() {
  useEffect(() => {
    const getClientIP = async () => {
      try {
        const response = await fetch('https://api.ipify.org?format=json');
        const { ip } = await response.json();
        iframeLocalStorage.setItem('client_ip', ip);
      } catch (error) {
        console.error('Failed to get client IP:', error);
      }
    };

    getClientIP();
  }, []);

  return (
    <PostHogProvider client={posthog}>
      <QueryClientProvider client={queryClient}>
        <ToastContainer
          position="top-center"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          style={{
            zIndex: 999999999,
            width: 'auto',
            minWidth: '340px',
            maxWidth: '520px',
            fontSize: '0.875rem',
          }}
        />
        <RouterProvider router={router} />
      </QueryClientProvider>
    </PostHogProvider>
  );
}

export default Root;
